/**
 * Keys (Redis): the project's keys (its prefix hidden) found by a pattern, each with its type and TTL; the one picked
 * opens with an editor for its type (text, fields, items, scored members; streams are read-only), its TTL, delete.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, Trash, X } from '@phosphor-icons/react';
import { ConfirmDialog, EmptyState, ErrorState, Loading, ReadOnlyBadge, errorText } from './DataCommon';
import { deleteRedisKey, getRedisKey, listRedisKeys, setRedisKey, setRedisTtl } from '../../services/DataService';
import { formatCount, ttlText } from '../../utils/dataModel';

const TYPES = ['string', 'hash', 'list', 'set', 'zset'];

/** The editor's rows for a value of a type. */
function rowsOf(type, value) {
  if (type === 'hash') { return Object.entries(value || {}).map(([field, v]) => ({ field, value: String(v ?? '') })); }
  if (type === 'list' || type === 'set') { return (value || []).map((v) => ({ value: String(v ?? '') })); }
  if (type === 'zset') { return (value || []).map((m) => ({ value: String(m.member ?? ''), score: String(m.score ?? 0) })); }
  return [];
}

/** The value a type's rows (or text) send. */
function valueOf(type, rows, text) {
  if (type === 'string') { return text; }
  if (type === 'hash') { return Object.fromEntries(rows.filter((r) => r.field !== '').map((r) => [r.field, r.value])); }
  if (type === 'zset') { return rows.filter((r) => r.value !== '').map((r) => ({ member: r.value, score: Number(r.score) || 0 })); }
  return rows.filter((r) => type === 'list' || r.value !== '').map((r) => r.value);
}

function RowsEditor({ type, rows, onChange, readOnly }) {
  const set = (index, patch) => onChange(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  return (
    <div className="data-kv-editor">
      <table className="data-table is-compact">
        <thead>
          <tr>
            {type === 'hash' && <th scope="col">Field</th>}
            <th scope="col">{type === 'hash' ? 'Value' : type === 'list' ? 'Item' : 'Member'}</th>
            {type === 'zset' && <th scope="col">Score</th>}
            {!readOnly && <th className="data-check-col"><span className="data-sr">Remove</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {type === 'hash' && (
                <td><input aria-label={`Field ${index + 1}`} className="data-cell-input data-mono" onChange={(e) => set(index, { field: e.target.value })} readOnly={readOnly} value={row.field} /></td>
              )}
              <td><input aria-label={`${type === 'hash' ? 'Value' : type === 'list' ? 'Item' : 'Member'} ${index + 1}`} className="data-cell-input data-mono" onChange={(e) => set(index, { value: e.target.value })} readOnly={readOnly} value={row.value} /></td>
              {type === 'zset' && (
                <td><input aria-label={`Score ${index + 1}`} className="data-cell-input data-mono" inputMode="decimal" onChange={(e) => set(index, { score: e.target.value })} readOnly={readOnly} value={row.score} /></td>
              )}
              {!readOnly && (
                <td className="data-check-col">
                  <button aria-label={`Remove row ${index + 1}`} className="data-btn is-ghost is-tiny" onClick={() => onChange(rows.filter((_, i) => i !== index))} type="button"><X aria-hidden size={12} /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!readOnly && (
        <button className="data-btn is-ghost is-link" onClick={() => onChange([...rows, type === 'hash' ? { field: '', value: '' } : type === 'zset' ? { value: '', score: '0' } : { value: '' }])} type="button">
          + {type === 'hash' ? 'Field' : type === 'list' ? 'Item' : 'Member'}
        </button>
      )}
    </div>
  );
}

RowsEditor.propTypes = { onChange: PropTypes.func.isRequired, readOnly: PropTypes.bool, rows: PropTypes.array.isRequired, type: PropTypes.string.isRequired };

export default function RedisView({ projectId, store, readOnly = false, onOpenQuery, onApplied = null }) {
  const [pattern, setPattern] = useState('*');
  const [match, setMatch] = useState('*');
  const [keys, setKeys] = useState(null);
  const [cursor, setCursor] = useState('0');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState(() => new Set());
  const [current, setCurrent] = useState(null); // { key, type, ttl, value, isNew }
  const [detailError, setDetailError] = useState(null);
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [ttlInput, setTtlInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [confirm, setConfirm] = useState(null); // [keys]
  const canWrite = !readOnly && !store.readOnly;

  const load = useCallback(async (from = '0') => {
    setLoading(true);
    setError(null);
    try {
      const result = await listRedisKeys(projectId, { match, cursor: from, count: 100 });
      setKeys((prev) => (from !== '0' && prev ? [...prev, ...(result.keys || [])] : result.keys || []));
      setCursor(String(result.cursor ?? '0'));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, match]);

  useEffect(() => { load('0'); }, [load]);

  const fill = (entry) => {
    setCurrent(entry);
    setText(entry.type === 'string' ? String(entry.value ?? '') : '');
    setRows(rowsOf(entry.type, entry.value));
    setTtlInput(entry.ttl > 0 ? String(entry.ttl) : '');
    setNotice(null);
    setDetailError(null);
  };

  const openKey = async (key) => {
    setDetailError(null);
    setCurrent({ key, loading: true });
    try {
      fill(await getRedisKey(projectId, key));
    } catch (e) {
      setCurrent({ key, failed: true });
      setDetailError(errorText(e));
    }
  };

  const save = async () => {
    if (!current?.key || current.type === 'stream') { return; }
    setBusy(true);
    setDetailError(null);
    try {
      const ttl = current.isNew && ttlInput ? Number(ttlInput) : undefined;
      await setRedisKey(projectId, current.key, { type: current.type, value: valueOf(current.type, rows, text), ...(ttl ? { ttl } : {}) });
      setNotice('Saved');
      const fresh = await getRedisKey(projectId, current.key).catch(() => null);
      if (fresh) { fill(fresh); setNotice('Saved'); }
      load('0');
    } catch (e) {
      setDetailError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const applyTtl = async (ttl) => {
    setBusy(true);
    setDetailError(null);
    try {
      await setRedisTtl(projectId, current.key, ttl);
      setCurrent({ ...current, ttl: ttl ?? -1 });
      setNotice(ttl ? `Expires in ${ttlText(ttl).replace('TTL ', '')}` : 'No expiry now');
      load('0');
    } catch (e) {
      setDetailError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const answers = await Promise.all(confirm.map((key) => deleteRedisKey(projectId, key)));
      // A restore point taken before the delete: the tab says so (DataPanel's bar).
      const saved = (answers || []).find((a) => a?.restorePoint || a?.restorePointWarning);
      if (saved) { onApplied?.({ restorePoint: saved.restorePoint || null, restorePointWarning: saved.restorePointWarning || null, summary: `Deleted ${confirm.length} key${confirm.length === 1 ? '' : 's'}` }); }
      if (current && confirm.includes(current.key)) { setCurrent(null); }
      setChecked(new Set());
      setConfirm(null);
      load('0');
    } catch (e) {
      setDetailError(errorText(e));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key) => setChecked((prev) => { const next = new Set(prev); if (next.has(key)) { next.delete(key); } else { next.add(key); } return next; });
  const editable = canWrite && current && !current.loading && !current.failed && current.type !== 'stream';

  return (
    <div className="data-view" data-testid="redis-view">
      <div className="data-view-head">
        <h1>Keys</h1>
        {keys && <span className="data-pill">{formatCount(keys.length)}{cursor !== '0' ? '+' : ''} key{keys.length === 1 ? '' : 's'}</span>}
        {!canWrite && <ReadOnlyBadge reason={readOnly ? 'You are a viewer of this project' : 'This store is read-only here'} />}
        <span className="data-grow" />
        <button className="data-btn" onClick={() => onOpenQuery?.(`SCAN 0 MATCH ${match} COUNT 100`)} type="button">Query</button>
        {canWrite && (
          <button className="data-btn is-primary" onClick={() => fill({ key: '', type: 'string', ttl: -1, value: '', isNew: true })} type="button">
            <Plus aria-hidden size={15} />Key
          </button>
        )}
      </div>

      <div className="data-split">
        <section aria-label="Keys" className="data-card data-keys">
          <form className="data-card-head" onSubmit={(e) => { e.preventDefault(); setMatch(pattern.trim() || '*'); }}>
            <label className="data-sr" htmlFor="data-key-pattern">Key pattern</label>
            <input className="data-input data-mono is-wide" id="data-key-pattern" onChange={(e) => setPattern(e.target.value)} placeholder="product:*" value={pattern} />
            <button className="data-btn" type="submit">Find</button>
          </form>
          {error && <ErrorState error={error} onRetry={() => load('0')} />}
          {!error && !keys && loading && <Loading label="Loading the keys…" />}
          {keys && !keys.length && <EmptyState title={match === '*' ? 'No keys yet' : 'No key matches'} />}
          {keys && keys.length > 0 && (
            <ul className="data-plain-list">
              {keys.map((k) => (
                <li className={`data-kv${current?.key === k.key ? ' is-on' : ''}`} key={k.key}>
                  {canWrite && <input aria-label={`Select ${k.key}`} checked={checked.has(k.key)} onChange={() => toggle(k.key)} type="checkbox" />}
                  <button className="data-kv-btn" data-testid="redis-key" onClick={() => openKey(k.key)} type="button">
                    <span className="data-mono">{k.key}</span>
                    <span className="data-muted">{k.type} · {ttlText(k.ttl)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="data-actions is-padded">
            {cursor !== '0' && <button className="data-btn" disabled={loading} onClick={() => load(cursor)} type="button">Load more</button>}
            {canWrite && checked.size > 0 && (
              <button className="data-btn is-danger" onClick={() => setConfirm([...checked])} type="button"><Trash aria-hidden size={15} />Delete {checked.size} key{checked.size === 1 ? '' : 's'}…</button>
            )}
          </div>
        </section>

        <section aria-labelledby="data-key-title" className="data-card data-key-detail">
          {!current && <EmptyState title="Pick a key">Its value opens here.</EmptyState>}
          {current?.loading && <Loading label={`Loading ${current.key}…`} />}
          {current && !current.loading && (
            <>
              <div className="data-card-head">
                {current.isNew ? (
                  <>
                    <h2 className="data-sr" id="data-key-title">New key</h2>
                    <label className="data-label" htmlFor="data-new-key">Key</label>
                    <input className="data-input data-mono is-wide" id="data-new-key" onChange={(e) => setCurrent({ ...current, key: e.target.value })} placeholder="product:1182" value={current.key} />
                    <label className="data-label" htmlFor="data-new-type">Type</label>
                    <select className="data-input" id="data-new-type" onChange={(e) => fill({ ...current, type: e.target.value, value: e.target.value === 'string' ? '' : e.target.value === 'hash' ? {} : [] })} value={current.type}>
                      {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </>
                ) : (
                  <>
                    <h2 className="data-mono" id="data-key-title">{current.key}</h2>
                    {current.type && <span className="data-pill">{current.type}</span>}
                    {!current.failed && <span className="data-pill">{ttlText(current.ttl)}</span>}
                  </>
                )}
                {notice && <span className="data-pill is-ok" role="status">{notice}</span>}
              </div>
              {!current.failed && (current.type === 'string' ? (
                <>
                  <label className="data-sr" htmlFor="data-key-value">Value</label>
                  <textarea className="data-textarea data-mono" id="data-key-value" onChange={(e) => setText(e.target.value)} readOnly={!editable} rows={10} spellCheck={false} value={text} />
                </>
              ) : current.type === 'stream' ? (
                <pre className="data-text-preview data-mono" data-testid="stream-value">{JSON.stringify(current.value, null, 2)}</pre>
              ) : (
                <RowsEditor onChange={setRows} readOnly={!editable} rows={rows} type={current.type} />
              ))}
              {detailError && <p className="data-error-text" role="alert">{detailError}</p>}
              {canWrite && !current.failed && (
                <div className="data-actions">
                  {current.type !== 'stream' && (
                    <button className="data-btn is-primary" disabled={busy || !current.key} onClick={save} type="button">{busy ? 'Saving…' : current.isNew ? 'Add key' : 'Save'}</button>
                  )}
                  <label className="data-label" htmlFor="data-ttl">TTL (seconds)</label>
                  <input className="data-input is-narrow" id="data-ttl" inputMode="numeric" onChange={(e) => setTtlInput(e.target.value.replace(/[^0-9]/g, ''))} placeholder="none" value={ttlInput} />
                  {!current.isNew && (
                    <>
                      <button className="data-btn" disabled={busy || !ttlInput} onClick={() => applyTtl(Number(ttlInput))} type="button">Set TTL</button>
                      {current.ttl > 0 && <button className="data-btn" disabled={busy} onClick={() => applyTtl(null)} type="button">Remove TTL</button>}
                      <span className="data-grow" />
                      <button className="data-btn is-danger" onClick={() => setConfirm([current.key])} type="button">Delete…</button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {confirm && (
        <ConfirmDialog busy={busy} onClose={() => setConfirm(null)} onConfirm={remove} title={`Delete ${confirm.length === 1 ? confirm[0] : `${confirm.length} keys`}?`}>
          <p>{confirm.length === 1 ? 'The key goes' : 'They go'} from Redis for good. Your services may write {confirm.length === 1 ? 'it' : 'them'} again.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

RedisView.propTypes = {
  onApplied: PropTypes.func,
  onOpenQuery: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  store: PropTypes.object.isRequired,
};
